import { useRegionContext } from "@app/lib/auth/RegionContext";
import { useUser } from "@app/lib/swr/user";
import logger from "@app/logger/logger";
import { Novu } from "@novu/js";
import { useEffect, useMemo, useState } from "react";

// The SPA build env historically ships placeholder "dummy" values for the
// NEXT_PUBLIC_NOVU_* vars; treat them (and empty strings) as unset so consumers
// hide their notification UI instead of initializing a broken client.
const normalizeNovuEnvValue = (
  value: string | undefined
): string | undefined => {
  return value && value !== "dummy" ? value : undefined;
};

const getNovuEnvForRegion = ({
  isEuRegion,
}: {
  isEuRegion: boolean;
}): {
  applicationIdentifier: string | undefined;
  apiUrl: string | undefined;
  socketUrl: string | undefined;
} => {
  if (isEuRegion) {
    return {
      applicationIdentifier:
        normalizeNovuEnvValue(
          process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER_EU
        ) ??
        normalizeNovuEnvValue(
          process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER
        ),
      apiUrl:
        normalizeNovuEnvValue(process.env.NEXT_PUBLIC_NOVU_API_URL_EU) ??
        normalizeNovuEnvValue(process.env.NEXT_PUBLIC_NOVU_API_URL),
      socketUrl:
        normalizeNovuEnvValue(
          process.env.NEXT_PUBLIC_NOVU_WEBSOCKET_API_URL_EU
        ) ??
        normalizeNovuEnvValue(process.env.NEXT_PUBLIC_NOVU_WEBSOCKET_API_URL),
    };
  }

  return {
    applicationIdentifier:
      normalizeNovuEnvValue(
        process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER_US
      ) ??
      normalizeNovuEnvValue(
        process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER
      ),
    apiUrl:
      normalizeNovuEnvValue(process.env.NEXT_PUBLIC_NOVU_API_URL_US) ??
      normalizeNovuEnvValue(process.env.NEXT_PUBLIC_NOVU_API_URL),
    socketUrl:
      normalizeNovuEnvValue(
        process.env.NEXT_PUBLIC_NOVU_WEBSOCKET_API_URL_US
      ) ??
      normalizeNovuEnvValue(process.env.NEXT_PUBLIC_NOVU_WEBSOCKET_API_URL),
  };
};

export const useNovuClient = () => {
  const { user } = useUser();
  const regionContext = useRegionContext();
  const [novuClient, setNovuClient] = useState<Novu | null>(null);

  const novuConfig = useMemo(() => {
    const regionName = regionContext?.regionInfo.name;
    const regionUrl = regionContext?.regionInfo.url;

    const isEuRegion =
      regionName?.startsWith("europe-") ||
      (regionUrl ? regionUrl.includes("://eu.") : false);

    return getNovuEnvForRegion({ isEuRegion: Boolean(isEuRegion) });
  }, [regionContext?.regionInfo.name, regionContext?.regionInfo.url]);

  // Distinguishes "Novu is not configured on this deployment" (consumers hide
  // their notification UI) from "client still initializing" (consumers wait).
  const isNovuConfigured = Boolean(
    novuConfig.applicationIdentifier &&
      novuConfig.apiUrl &&
      novuConfig.socketUrl
  );

  useEffect(() => {
    if (!user?.subscriberHash || !user?.sId) {
      return;
    }

    const { applicationIdentifier, apiUrl, socketUrl } = novuConfig;
    if (!applicationIdentifier || !apiUrl || !socketUrl) {
      // Novu powers in-app notifications and is optional. When its env is not
      // configured (e.g. local dev, or a deploy without notifications), skip
      // initialization and degrade gracefully — consumers already handle a null
      // client. Throwing here bubbles to the error boundary and takes the whole
      // app down for something non-essential.
      logger.warn(
        "Novu notifications disabled: NEXT_PUBLIC_NOVU_* env is not configured."
      );
      return;
    }

    setNovuClient(
      new Novu({
        applicationIdentifier,
        apiUrl,
        socketUrl,
        subscriber: user.sId,
        subscriberHash: user.subscriberHash,
      })
    );
  }, [novuConfig, user?.subscriberHash, user?.sId]);

  return { novuClient, isNovuConfigured };
};
